/**
 * Rebuy (widget 316875 + Smart Cart) – FR / NL translations.
 * Swaps English text nodes in place and re-applies whenever Rebuy re-renders
 * (e.g. Add -> Adding... -> Added!). Recommended product titles are replaced
 * with Shopify's localized titles, since Rebuy serves its own cached catalog.
 */
(function () {
  const translations = {
    fr: {
      exact: {
        Add: 'Ajouter',
        'Adding...': 'Ajout...',
        'Added!': 'Ajouté !',
        'Sold Out': 'Épuisé',
        'You may also like': 'Vous pourriez aussi aimer',
        'Not sure where to start?': 'Vous ne savez pas par où commencer ?',
        'Your Cart': 'Votre panier',
        'Your cart is empty!': 'Votre panier est vide !',
        'Add your favorite items to your cart.': 'Ajoutez vos articles préférés à votre panier.',
        'Shop Now': 'Acheter maintenant',
        Subtotal: 'Sous-total',
        'Pay today': "Payer aujourd'hui",
        'Taxes included. No surprises at checkout.': 'Taxes incluses. Aucune surprise au moment de payer.',
        'Checkout securely': 'Paiement sécurisé',
        'Safe, secure & ready for your daily ritual': 'Sûr, sécurisé et prêt pour votre rituel quotidien',
      },
      save: 'Économisez',
      upgrade: (n) => `Passez à l'abonnement et économisez ${n}`,
    },
    nl: {
      exact: {
        Add: 'Toevoegen',
        'Adding...': 'Toevoegen...',
        'Added!': 'Toegevoegd!',
        'Sold Out': 'Uitverkocht',
        'You may also like': 'Dit vind je misschien ook leuk',
        'Not sure where to start?': 'Weet je niet waar je moet beginnen?',
        'Your Cart': 'Je winkelwagen',
        'Your cart is empty!': 'Je winkelwagen is leeg!',
        'Add your favorite items to your cart.': 'Voeg je favoriete producten toe aan je winkelwagen.',
        'Shop Now': 'Nu winkelen',
        Subtotal: 'Subtotaal',
        'Pay today': 'Vandaag betalen',
        'Taxes included. No surprises at checkout.': 'Inclusief btw. Geen verrassingen bij het afrekenen.',
        'Checkout securely': 'Veilig afrekenen',
        'Safe, secure & ready for your daily ritual': 'Veilig, beveiligd en klaar voor je dagelijkse ritueel',
      },
      save: 'Bespaar',
      upgrade: (n) => `Upgrade naar abonnement en bespaar ${n}`,
    },
  };

  const lang = (document.documentElement.lang || '').toLowerCase().split('-')[0];
  const map = translations[lang];
  if (!map) return;

  // Any Rebuy widget (populated and empty-cart variants render as different
  // widget instances/content) plus the Smart Cart drawer.
  const selector = '[data-rebuy-id], [id^="rebuy-widget-"], .rebuy-widget, #rebuy-cart, .rebuy-cart';
  // Remember the English original per text node so re-renders still match.
  const originals = new WeakMap();
  // Rebuy sometimes serves lowercase copy ("not sure where to start?"), so match case-insensitively.
  const exactLower = {};
  Object.keys(map.exact).forEach((k) => { exactLower[k.toLowerCase()] = map.exact[k]; });

  function translateNode(node) {
    const original = originals.get(node) || node.nodeValue.trim();
    let translated = exactLower[original.toLowerCase()];
    if (!translated) {
      const save = original.match(/^Save\s+(.+)$/i);
      if (save) translated = `${map.save} ${save[1]}`;
    }
    if (!translated) {
      const up = original.match(/^Upgrade to Subscription & Save\s+(.+)$/i);
      if (up) translated = map.upgrade(up[1]);
    }
    if (!translated) return;
    originals.set(node, original);
    if (node.nodeValue !== translated) node.nodeValue = translated;
  }

  // Rebuy's catalog is not locale-aware: look up Shopify's localized title.
  // Cache is per handle, but every title element is re-checked on each pass,
  // because Rebuy re-uses/re-renders nodes with the English title on cart changes.
  const titleCache = {}; // handle -> Promise<title|null>
  const resolved = {}; // handle -> title (sync once known)

  function setTitle(el, title) {
    const textNode = Array.from(el.childNodes).find((n) => n.nodeType === 3 && n.nodeValue.trim());
    if (textNode) {
      if (textNode.nodeValue !== title) textNode.nodeValue = title;
    } else if (!el.children.length && el.textContent !== title) {
      el.textContent = title;
    }
  }

  function handleFor(el) {
    // Walk up to the nearest ancestor holding a product link.
    for (let node = el; node && node !== document.body; node = node.parentElement) {
      const link = node.matches('a[href*="/products/"]') ? node : node.querySelector('a[href*="/products/"]');
      if (link) {
        const m = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
        return m && m[1];
      }
    }
    return null;
  }

  function localizeTitles(container) {
    container.querySelectorAll('[class*="product-title"]').forEach((el) => {
      const handle = handleFor(el);
      if (!handle) return;
      if (resolved[handle]) return setTitle(el, resolved[handle]);
      if (!titleCache[handle]) {
        const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        titleCache[handle] = fetch(`${root}products/${handle}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
          .then((p) => {
            if (p && p.title) resolved[handle] = p.title;
            return resolved[handle] || null;
          });
      }
      // Re-translate the whole container once the title arrives, covering any
      // nodes that were re-rendered while the request was in flight.
      titleCache[handle].then((t) => { if (t) translate(container); });
    });
  }

  function translate(container) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue.trim()) translateNode(node);
    }
    localizeTitles(container);
  }

  // Widgets and the cart drawer are injected after load, so bind once on appearance.
  const bound = new WeakSet();
  function bindNew() {
    document.querySelectorAll(selector).forEach((container) => {
      if (bound.has(container)) return;
      bound.add(container);
      translate(container);
      let queued = false;
      const observer = new MutationObserver(() => {
        if (queued) return;
        queued = true;
        queueMicrotask(() => {
          queued = false;
          observer.disconnect();
          translate(container);
          observer.observe(container, { childList: true, subtree: true, characterData: true });
        });
      });
      observer.observe(container, { childList: true, subtree: true, characterData: true });
    });
  }

  bindNew();
  new MutationObserver(bindNew).observe(document.documentElement, { childList: true, subtree: true });
})();
